import React from 'react';
import balluffLogo from '../assets/images/Balluff.jpg';
import baumerLogo from '../assets/images/Baumer.jpg';
import bimbaLogo from '../assets/images/IMI_Bimba.png';
import cognexLogo from '../assets/images/Cognex.jpg';
import datalogicLogo from '../assets/images/DATALOGIC.jpg';
import dickiesLogo from '../assets/images/Dickies.png';
import eatonLogo from '../assets/images/EATON.jpg';
import fastestLogo from '../assets/images/Fastest.jpg';
import lappLogo from '../assets/images/LAPP_Kabel.png';
import littelfuseLogo from '../assets/images/Littelfuse.png';
import mapleLogo from '../assets/images/Maple_Systems.jpg';
import mitsubishiLogo from '../assets/images/Mitsubishi_Electric.png';
import norgrenLogo from '../assets/images/IMI_NORGREN.png';
import noshokLogo from '../assets/images/NOSHOK.jpg';
import omronLogo from '../assets/images/OMRON.png';
import phoenixLogo from '../assets/images/Phoenix_Contact.png';
import sharpieLogo from '../assets/images/Sharpie.png';
import sickLogo from '../assets/images/SICK.png';
import siemensLogo from '../assets/images/Siemens.png';
import smcLogo from '../assets/images/SMC.png';
import solahdLogo from '../assets/images/SOLAHD.png';
import swivellinkLogo from '../assets/images/Swivellink.jpg';
import teLogo from '../assets/images/TE.jpg';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';

const knownLogos = {
  AUTONICS: 'https://www.autonics.com/main_renewal/img/header/logo.svg',
  AVENTICS: 'https://upload.wikimedia.org/wikipedia/commons/8/85/Aventics_logo.png',
  BALLUFF: balluffLogo,
  BAUMER: baumerLogo,
  BIMBA: bimbaLogo,
  COGNEX: cognexLogo,
  DATALOGIC: datalogicLogo,
  DICKIES: dickiesLogo,
  EATON: eatonLogo,
  FASTEST: fastestLogo,
  LAPP_KABEL: lappLogo,
  LITTELFUSE: littelfuseLogo,
  MAPLE_SYSTEMS: mapleLogo,
  MITSUBISHI: mitsubishiLogo,
  MITSUBISHI_ELECTRIC: mitsubishiLogo,
  NORGREN: norgrenLogo,
  NOSHOK: noshokLogo,
  OMRON: omronLogo,
  PHOENIX_CONTACT: phoenixLogo,
  SHARPIE: sharpieLogo,
  SICK: sickLogo,
  SIEMENS: siemensLogo,
  SMC: smcLogo,
  SOLAHD: solahdLogo,
  SWIVELLINK: swivellinkLogo,
  TE_CONNECTIVITY: teLogo,
};

const cleanKey = (value) => String(value || '')
  .trim()
  .replace(/\.(jpg|jpeg|png|webp|svg)$/i, '')
  .replace(/[\s.\-–—]+/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_|_$/g, '');

// Uploaded logos use opaque server-generated keys such as
// `brand-1786715696033-877220788`. Normalizing that key would change the
// actual filename, so only remove a possible path and file extension here.
const cleanFileKey = (value) => String(value || '')
  .trim()
  .replace(/^.*[\\/]/, '')
  .replace(/\.(jpg|jpeg|png|webp|svg)$/i, '');

const titleCaseKey = (value) => cleanKey(value)
  .toLowerCase()
  .split('_')
  .map((part) => part ? `${part[0].toUpperCase()}${part.slice(1)}` : '')
  .join('_');

export const getBrandLogoCandidates = (brand) => {
  const exactImageKey = cleanFileKey(brand?.brand_image);
  const legacyImageKey = cleanKey(brand?.brand_image);
  const nameKey = cleanKey(brand?.brand_name);
  const knownLogoKey = (legacyImageKey || nameKey).toUpperCase();
  const bases = [...new Set([
    exactImageKey,
    legacyImageKey,
    legacyImageKey.toUpperCase(),
    titleCaseKey(legacyImageKey),
    nameKey,
    nameKey.toUpperCase(),
    titleCaseKey(nameKey),
  ].filter(Boolean))];
  const serverCandidates = bases.flatMap((base) => ['jpg', 'png', 'jpeg', 'webp'].map((extension) => `${API_BASE}/data/${encodeURIComponent(base)}.${extension}`));
  return [...new Set([knownLogos[knownLogoKey], ...serverCandidates].filter(Boolean))];
};

export default function BrandLogoImage({ brand, className, alt, loading = 'lazy', onLoad, onExhausted }) {
  const candidates = getBrandLogoCandidates(brand);

  if (!candidates.length) return null;

  const handleError = (event) => {
    const image = event.currentTarget;
    const nextIndex = Number(image.dataset.logoIndex || 0) + 1;
    if (nextIndex >= candidates.length) {
      image.style.display = 'none';
      onExhausted?.();
      return;
    }
    image.dataset.logoIndex = String(nextIndex);
    image.src = candidates[nextIndex];
  };

  return (
    <img
      src={candidates[0]}
      data-logo-index="0"
      alt={alt ?? `${brand?.brand_name || 'Brand'} logo`}
      className={className}
      loading={loading}
      onLoad={onLoad}
      onError={handleError}
    />
  );
}
